import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { Lock, BookOpen, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function LecturesPage() {
  const { user } = useAuth();
  const { t, pick } = useI18n();
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lectures");
        setLectures(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load lectures");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <main data-testid="lectures-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="label-small text-[#8B3A3A] mb-3">{t("lectures.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">{t("lectures.heading")}</h1>
          <p className="text-[#4A4238] mt-3 max-w-2xl">{t("lectures.intro")}</p>
        </div>
        <span className="grade-pill" data-testid="lectures-user-grade">{t("lectures.your_grade")} {user?.grade}</span>
      </header>

      {loading ? (
        <p className="text-[#7A7061] italic">{t("rituals.unsealing")}</p>
      ) : (
        <ol className="border border-[#C1B49A]" data-testid="lectures-list">
          {lectures.map((l, idx) => {
            const num = String(idx + 1).padStart(2, "0");
            const Locked = l.locked;
            const body = (
              <div className="grid grid-cols-12 gap-6 p-6 md:p-8 items-center">
                <div className="col-span-2 md:col-span-1 font-display text-4xl md:text-5xl text-[#8B3A3A] italic">{num}</div>
                <div className="col-span-8 md:col-span-9">
                  <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#7A7061] mb-2">{t("lectures.grade_label")} · {l.grade}</p>
                  <h3 className="font-display text-2xl md:text-3xl text-[#1A1918] leading-tight">{pick(l, "title")}</h3>
                </div>
                <div className="col-span-2 flex items-center justify-end">
                  {Locked ? (
                    <span className="inline-flex items-center gap-2 text-[#7A7061]"><Lock size={18} /></span>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[#8B3A3A]"><BookOpen size={18} /><ArrowUpRight size={16} /></span>
                  )}
                </div>
              </div>
            );
            return (
              <li
                key={l.id}
                className="border-b border-[#C1B49A] last:border-b-0 bg-[#EAE0CA]/40"
                data-testid={`lecture-row-${l.grade}`}
              >
                {Locked ? (
                  <div className="opacity-60 cursor-not-allowed" data-testid={`lecture-locked-${l.grade}`}>{body}</div>
                ) : (
                  <Link to={`/lectures/${l.id}`} className="block hover:bg-[#EAE0CA]/80 transition-colors" data-testid={`lecture-open-${l.grade}`}>
                    {body}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </main>
  );
}
